<template>
  <v-sheet>
    <div
      class="drop-zone"
      v-if="showDropZone"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click="triggerFileInput">
      <img class="me-4" width="80" src="@Assets/svgs/Document.svg" alt="document" />
      <div class="drop-zone-text">
        <p>Drag and drop your Document here</p>
        <v-btn color="primary" rounded>
          <v-icon class="me-2">mdi-tray-arrow-up</v-icon>
          Click to browse
        </v-btn>
      </div>
      <!-- Security: Only allow safe document types -->
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.tif"
        @change="handleFileChange"
        multiple />
    </div>
    <v-sheet>
      <v-row class="ma-0">
        <v-col
          class="fileCard d-flex align-center mt-3 py-1"
          cols="12"
          v-for="(file, index) in uploadedDocs"
          :key="index">
          <img
            width="36"
            height="36"
            src="@Assets/images/img.png"
            alt="img icon"
            v-if="file.file?.type === 'image/png' || isImage(file.fileName)" />
          <img
            width="36"
            height="36"
            src="@Assets/images/pdf.png"
            alt="pdf icon"
            v-if="
              file.file?.type === 'application/pdf' || file.fileName?.split('.').pop() === 'pdf'
            " />
          <img
            width="36"
            height="36"
            src="@Assets/images/excel.png"
            alt="pdf icon"
            v-if="
              file.file?.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
              file.file?.type === 'application/vnd.ms-excel' ||
              file.fileName?.split('.').pop() === 'xls' ||
              file.fileName?.split('.').pop() === 'xlsx'
            " />
          <img
            width="36"
            height="36"
            src="@Assets/images/docx.png"
            alt="docx icon"
            v-if="
              file.file?.type ===
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              ['docx', 'doc'].includes(file.fileName?.split('.').pop())
            " />
          <span class="font-weight-medium mx-2">
            {{ file.file ? file.file.name : file.fileName }}</span
          >
          <div
            class="progress-container flex-grow-1 d-flex justify-space-between align-center"
            v-if="isUploading && file.file">
            <div class="d-flex align-center mx-2">
              <v-progress-circular
                :width="3"
                :size="20"
                color="primary"
                indeterminate></v-progress-circular>
              <p>{{ Math.round(uploadProgress) }}% Uploading</p>
            </div>

            <v-btn color="primary" small outlined rounded @click="cancelUpload">Cancel</v-btn>
          </div>
          <div class="d-flex flex-grow-1 align-center" v-if="file.hasUploadError">
            <p class="mb-0 red--text caption mt-1">{{ file.uploadError }}.</p>
          </div>
          <v-spacer v-if="(!isUploading && !file.hasUploadError) || !file.file"></v-spacer>

          <v-btn icon color="primary" rounded @click="openDocument(file)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn
            v-if="!isUploading && !file.file"
            color="primary"
            icon
            small
            rounded
            @click="downloadFile(file)">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn
            v-if="(!isUploading && !file.hasUploadError) || !file.file"
            icon
            color="error"
            rounded
            @click="clearFile(file, index)">
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
    <DocumentWorkspaceDialog ref="documentWorkspace" />
  </v-sheet>
</template>

<script>
  import schedulerService from '@/features/Task/service/scheduler.service';
  import DocumentWorkspaceDialog from '@/features/Documents/components/DocumentWorkspaceDialog.vue';
  import { isBlockedExtension } from '@/utils/fileValidation';
  import { log, warn, error } from '@/utils/debugLogger';
  /* eslint-disable */
  export default {
    name: 'FileDropZone',
    components: {
      DocumentWorkspaceDialog,
    },
    data() {
      return {
        isUploading: false,
        uploadProgress: 0,
        uploadInterval: null,
        backgroundImage: '',
        showDropZone: true,
        isUploadComplete: false,
        uploadedFilesLinks: [],
        uploadedDocs: [],
      };
    },
    props: {
      uploadedFiles: {
        type: Array,
        default: () => [],
      },
    },
    computed: {
      uploadingContainerStyle() {
        return {
          backgroundImage: this.backgroundImage ? `url(${this.backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      },
    },
    methods: {
      handleDrop(event) {
        this.isUploading = true;
        this.$emit('uploading', true);
        this.uploadProgress = 0;

        const allFiles = Array.from(event.dataTransfer.files);
        const blockedFiles = allFiles.filter(f => isBlockedExtension(f.name));
        const files = allFiles.filter(f => !isBlockedExtension(f.name));

        if (blockedFiles.length > 0) {
          this.$emit('blocked-files', blockedFiles.map(f => f.name));
        }

        if (files.length === 0) {
          this.isUploading = false;
          this.$emit('uploading', false);
          return;
        }
        this.uploadDocument(files);
      },
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      handleFileChange(event) {
        const files = event.target.files;
        if (files.length > 0) {
          this.startUpload(event);
        }
      },

      cancelUpload() {
        clearInterval(this.uploadInterval);
        this.isUploading = false;
        this.$emit('uploading', false);
        this.showDropZone = true;
        this.uploadProgress = 0;
        this.uploadedDocs.pop();
      },
      clearFile(file, index) {
        const hasFileWithValue = this.uploadedDocs.some((obj) => obj.file);
        if (hasFileWithValue) {
          this.uploadedFilesLinks.splice(index, 1);
          this.uploadedDocs.splice(index, 1);
          this.$emit('files-uploaded', this.uploadedFilesLinks);
        } else {
          this.uploadedDocs.splice(index, 1);
          let docs = this.uploadedDocs.map((doc) => ({
            id: doc.id,
            url: doc.storageReference,
            fileName: doc.fileName,
          }));
          this.$emit('files-uploaded', docs);
        }

        // this.uploadedFilesLinks.splice(index, 1);
      },
      clearAllFiles() {
        this.uploadedFiles = [];
        this.uploadedDocs = [];
        this.uploadedFilesLinks = [];
        this.$emit('files-uploaded', this.uploadedFilesLinks);
      },
      downloadFile(file) {
        fetch(file.storageReference)
          .then((response) => response.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.fileName; // Use fileName if available, otherwise default
            a.click();
            URL.revokeObjectURL(url); // Clean up
          })
          .catch((err) => {
            error('FileDropZone', 'Error downloading file', err);
          });
      },
      startUpload(event) {
        this.isUploading = true;
        this.$emit('uploading', true);
        this.uploadProgress = 0;

        const files = Array.from(event.target.files);
        this.uploadDocument(files);
      },
      uploadDocument(files) {
        const intervalDuration = 100; // Interval duration in milliseconds
        const totalDuration = 3500; // Total duration in milliseconds
        const incrementValue = 100 / (totalDuration / intervalDuration);

        if (this.uploadedFiles.length > 0) {
          this.uploadedDocs = this.uploadedFiles.concat(
            files.map((file) => ({
              file,
              hasUploadError: false,
              uploadError: '',
            })),
          );
        } else {
          this.uploadedDocs = files.map((file) => ({
            file,
            hasUploadError: false,
            uploadError: '',
          }));
        }
        this.uploadedDocs.forEach((file) => {
          if (file.file) {
            schedulerService
              .uploadDocument(file.file)
              .then((res) => {
                let data = res.data;

                this.isUploadComplete = true;
                this.uploadedFilesLinks.push({
                  id: null,
                  url: data.storageReference,
                  fileName: data.fileName,
                  size: data.size,
                });
                this.$emit('files-uploaded', this.uploadedFilesLinks);
                this.$refs.fileInput.value = '';
              })
              .catch((err) => {
                file.uploadError = err.message;
                file.hasUploadError = true;

                error('FileDropZone', 'Upload failed', err);
                this.isUploading = false;
              });
          } else {
            this.uploadedFilesLinks.push({
              id: file.id,
              url: file.storageReference,
              fileName: file.fileName,
              size: file.size,
            });
            this.$emit('files-uploaded', this.uploadedFilesLinks);
          }
        });

        this.uploadInterval = setInterval(() => {
          if (this.uploadProgress < 100) {
            this.uploadProgress += incrementValue;
          } else {
            clearInterval(this.uploadInterval);
            this.isUploading = false;
          }
        }, intervalDuration);
      },
      openDocument(file) {
        // A saved document loads in the workspace; a file not saved yet is shown as it is.
        if (file.id) this.$refs.documentWorkspace.open({ documentId: file.id });
        else this.$refs.documentWorkspace.open({ document: file, readonly: true });
      },      
      isImage(fileName) {
        if (!fileName) return false;

        // check file extension
        // Note: SVG removed for security (can contain executable JavaScript)
        const extensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp'];
        const ext = fileName.split('.').pop().toLowerCase();
        return extensions.includes(ext);
      }
    },
    watch: {
      uploadedFiles: {
        handler: function (val) {
          this.uploadedDocs = val;
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .drop-zone {
    border: 2px dashed #d4dfc5;
    border-radius: 7px;
    background-color: var(--v-very_light_green-base);
    padding: 20px;
    text-align: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .theme--dark {
    .drop-zone {
      background-color: #303926;
      border: 2px dashed var(--v-primary-base);
    }
  }
  .drop-zone-text {
    margin-top: 10px;
    color: var(--v-primary-base);
  }
  .uploading-container {
    border: 2px solid #d4dfc5;
    border-radius: 7px;
    background-color: var(--v-very_light_green-base);
    margin-top: 20px;
    display: flex;
    align-items: center;
    background-size: cover;
    background-position: center;
    position: relative;
    // padding: 25px 40px;
    // min-height: 120px;
  }

  .overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1;
  }

  .content {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 2;
  }

  .progress-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .progress-container p {
    margin: 0 10px;
    // color: var(--v-bg_force_white-base);
  }

  .fileCard {
    border: 2px solid #d4dfc5;
    border-radius: 7px;
    background-color: var(--v-light-grey-base);
  }
</style>
